<script setup lang="ts">
import { PropertySchema } from "~/classes/entity"

const props = defineProps({
  files: null
})

const audioMetadata = computed(() => mapped(props.files, f => f.audioMetadata()))

function anyProperty(
  field: keyof FileAudioContainerMetadata,
) {
  return any(Object.values(audioMetadata.value), metadata => metadata?.[field])
}

function stringProperty(
  field: keyof FileAudioContainerMetadata,
): Record<string, EntityPropertyValue | undefined> {
  return mapped(audioMetadata.value, (metadata) => {
    if (metadata && field in metadata)
      return { type: PropertySchema.AnyStringValue, anyString: metadata[field] as any }
  })
}

function urlProperty(
  field: keyof FileAudioContainerMetadata,
): Record<string, EntityPropertyValue | undefined> {
  return mapped(audioMetadata.value, (metadata) => {
    if (metadata && field in metadata)
      return { type: PropertySchema.WebUrlValue, url: metadata[field] as any }
  })
}

function numberProperty(
  field: keyof FileAudioContainerMetadata,
): Record<string, EntityPropertyValue | undefined> {
  return mapped(audioMetadata.value, (metadata) => {
    if (metadata && field in metadata)
      return { type: PropertySchema.AnyIntegerValue, anyInteger: metadata[field] as any }
  })
}

function isSingleMultilineField(
  field: keyof FileAudioContainerMetadata,
) {
  if (len(audioMetadata.value) > 1)
    return false
  const value = first(audioMetadata.value)?.[field] ?? ""
  if (typeof value === "string")
    return len(value) > 24
  else return len(JSON.stringify(value)) > 24
}

const durations: Ref<Record<string, EntityPropertyValue | undefined>> = computed(() => mapped(props.files, (f) => {
  const durationMs = f.mediaDurationMs()
  if (isDefined(durationMs))
    return {
      type: PropertySchema.NonEmptyStringValue,
      nonEmptyString: msToHhMmSs(durationMs),
    }
}))

// TODO: get the real bitrate and add it to the audio metadata container
const bitRatesKbps: Ref<Record<string, EntityPropertyValue | undefined>> = computed(() => mapped(props.files, (f) => {
  const duration = f.mediaDurationMs()
  const fileSizeBytes = f.size()
  if (duration && fileSizeBytes) {
    return {
      type: PropertySchema.PositiveFloatValue,
      positiveFloat: round((fileSizeBytes * 8) / (duration)),
    }
  }
}))
</script>

<template>
  <panel-details-property-section class="section">
    <template #header>
      Audio Details
    </template>
    <panel-details-property-field
      :values="durations"
      label="Duration"
    />
    <panel-details-property-field
      :values="bitRatesKbps"
      label="Bit rate (Kbps)"
    />
    <panel-details-property-field
      v-if="anyProperty('title')"
      :multiline="isSingleMultilineField('title')"
      :values="stringProperty('title')"
      label="Title"
    />
    <panel-details-property-field
      v-if="anyProperty('album')"
      :multiline="isSingleMultilineField('album')"
      :values="stringProperty('album')"
      label="Album"
    />
    <panel-details-property-field
      v-if="anyProperty('albumArtist')"
      :multiline="isSingleMultilineField('albumArtist')"
      :values="stringProperty('albumArtist')"
      label="Album Artist"
    />
    <panel-details-property-field
      v-if="anyProperty('originalArtist')"
      :multiline="isSingleMultilineField('originalArtist')"
      :values="stringProperty('originalArtist')"
      label="Original Artist"
    />
    <panel-details-property-field
      v-if="anyProperty('beatsPerMinute')"
      :multiline="isSingleMultilineField('beatsPerMinute')"
      :values="numberProperty('beatsPerMinute')"
      label="Beats Per Minute"
    />
    <panel-details-property-field
      v-if="anyProperty('comment')"
      :multiline="isSingleMultilineField('comment')"
      :values="stringProperty('comment')"
      label="Comment"
    />
    <panel-details-property-field
      v-if="anyProperty('urlSource')"
      :multiline="isSingleMultilineField('urlSource')"
      :values="urlProperty('urlSource')"
      label="Source"
    />
    <panel-details-property-field
      v-if="anyProperty('composer')"
      :multiline="isSingleMultilineField('composer')"
      :values="stringProperty('composer')"
      label="Composer"
    />
    <panel-details-property-field
      v-if="anyProperty('copyright')"
      :multiline="isSingleMultilineField('copyright')"
      :values="stringProperty('copyright')"
      label="Copyright"
    />
    <panel-details-property-field
      v-if="anyProperty('encoder')"
      :multiline="isSingleMultilineField('encoder')"
      :values="stringProperty('encoder')"
      label="Encoder"
    />
    <panel-details-property-field
      v-if="anyProperty('encodedBy')"
      :multiline="isSingleMultilineField('encodedBy')"
      :values="stringProperty('encodedBy')"
      label="Encoded By"
    />
    <panel-details-property-field
      v-if="anyProperty('genre')"
      :multiline="isSingleMultilineField('genre')"
      :values="stringProperty('genre')"
      label="Genre"
    />
    <panel-details-property-field
      v-if="anyProperty('disc')"
      :multiline="isSingleMultilineField('disc')"
      :values="stringProperty('disc')"
      label="Disc"
    />
    <panel-details-property-field
      v-if="anyProperty('date')"
      :multiline="isSingleMultilineField('date')"
      :values="numberProperty('date')"
      label="Date"
    />
    <panel-details-property-field
      v-if="anyProperty('lyrics')"
      :multiline="isSingleMultilineField('lyrics')"
      :values="stringProperty('lyrics')"
      label="Lyrics"
    />
    <!-- TODO: after we find out what this field usually looks like (e.g. 0-1? 1-5?),
    then parse the rating into the official rating property -->
    <panel-details-property-field
      v-if="anyProperty('rating')"
      :multiline="isSingleMultilineField('rating')"
      :values="numberProperty('rating')"
      label="Rating"
    />
  </panel-details-property-section>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'
</style>
